import { useDisclosure } from "@chakra-ui/hooks";
import { Modal, ModalOverlay } from "@chakra-ui/modal";
import React from "react";
import { MdHighlightOff } from "react-icons/md";
import DatePicker from "react-datepicker";
import { Select as AntSelect } from "antd";

const FilterModal = ({
  users,
  setPage,
  setFilter,
  filter,
  setLocalFilters,
  filterData,
  localFilters,
  setClearClick,
}: {
  users?: any;
  setPage?: any;
  filter?: any;
  setFilter?: (e: any) => void;
  setLocalFilters?: (e: any) => void;
  localFilters?: any;
  filterData?: () => void;
  setClearClick?: () => void;
}) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [isCloseModal, setIsCloseModal] = React.useState<boolean>(false);
  const [isApplied, setIsApplied] = React.useState(false);
  const [isDirty, setIsDirty] = React.useState(false);

  const handleClose = () => {
    if (isDirty && !isApplied) {
      setFilter({ users: "", dates: null });
    }
    setIsDirty(false);
    setIsCloseModal(true);
    setTimeout(() => {
      setIsCloseModal(false);
      onClose();
    }, 300);
  };

  const hasActiveFilter = filter?.users?.length > 0 || filter?.dates;

  return (
    <>
      <button
        className="af-trigger-btn"
        onClick={() => {
          onOpen();
          setIsApplied(false);
        }}
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/>
        </svg>
        Apply Filter
        {hasActiveFilter && <span className="af-trigger-dot" />}
      </button>

      {hasActiveFilter && (
        <button
          className="af-clear-btn"
          onClick={() => {
            setFilter({ users: "", dates: null });
            setIsApplied(false);
            setClearClick();
          }}
        >
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
          </svg>
          Clear
        </button>
      )}

      <Modal isOpen={isOpen} onClose={() => {}}>
        <ModalOverlay bg="rgba(15,23,42,0.55)" backdropFilter="blur(4px)" />

        <div className={`afm-panel${isOpen && !isCloseModal ? " afm-panel--open" : " afm-panel--close"}`}>

          {/* Decorative top bar */}
          <div className="afm-top-glow" />

          {/* Header */}
          <div className="afm-header">
            <div className="afm-header-brand">
              <div className="afm-header-logo">
                <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/>
                </svg>
              </div>
              <div>
                <h2 className="afm-header-title">Filter Audit Logs</h2>
                <p className="afm-header-sub">Narrow results by user or date range</p>
              </div>
            </div>
            <button className="afm-close-btn" onClick={handleClose} aria-label="Close">
              <MdHighlightOff size={22} />
            </button>
          </div>

          {/* Body */}
          <div className="afm-body">

            {/* User filter */}
            <div className="afm-section">
              <div className="afm-section-icon afm-section-icon--green">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                  <circle cx="12" cy="7" r="4"/>
                </svg>
              </div>
              <div className="afm-section-content">
                <label className="afm-label">
                  Filter by User
                  {filter?.users && <span className="afm-active-dot" />}
                </label>
                <p className="afm-hint">Show logs from a specific user</p>
                <AntSelect
                  showSearch
                  allowClear
                  placeholder="Select a user…"
                  optionFilterProp="children"
                  filterOption={(input, option) => {
                    const label = String(option?.label ?? "");
                    return label.toLowerCase().includes(input.toLowerCase());
                  }}
                  className="afm-ant-select"
                  style={{ width: "100%" }}
                  options={users?.map((user: any) => ({
                    value: user.id,
                    label: `${user.first_name} ${user.last_name}`,
                  }))}
                  value={filter?.users || undefined}
                  onChange={(value: any) => {
                    setIsDirty(true);
                    setFilter((prev: any) => ({ ...prev, users: value }));
                    setLocalFilters?.((prev: any) => ({ ...prev, users: value }));
                  }}
                  notFoundContent="No users found!"
                />
              </div>
            </div>

            <div className="afm-divider" />

            {/* Date filter */}
            <div className="afm-section">
              <div className="afm-section-icon afm-section-icon--purple">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
                  <line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/>
                  <line x1="3" y1="10" x2="21" y2="10"/>
                </svg>
              </div>
              <div className="afm-section-content">
                <label className="afm-label">
                  Date Range
                  {filter?.dates && <span className="afm-active-dot" />}
                </label>
                <p className="afm-hint">Filter logs within a specific date range</p>
                <div className="afm-datepicker-wrap">
                  <DatePicker
                    className="afm-datepicker"
                    placeholderText="Select date range…"
                    startDate={filter?.start_date ? new Date(filter?.start_date) : null}
                    endDate={filter?.end_date ? new Date(filter?.end_date) : null}
                    selectsRange={true}
                    isClearable={true}
                    onChange={(dateRange) => {
                      setIsDirty(true);
                      const [a, b] = dateRange as [Date | null, Date | null];
                      setFilter((prev: any) => ({
                        ...prev,
                        start_date: a,
                        end_date: b,
                        dates: a != null && b != null ? [a, b] : null,
                      }));
                    }}
                  />
                </div>
              </div>
            </div>

            {/* Active filters summary */}
            {hasActiveFilter && (
              <div className="afm-summary">
                <p className="afm-summary-label">Active filters:</p>
                <div className="afm-summary-chips">
                  {filter?.users && (
                    <span className="afm-chip afm-chip--green">
                      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                        <circle cx="12" cy="7" r="4"/>
                      </svg>
                      User selected
                    </span>
                  )}
                  {filter?.dates && (
                    <span className="afm-chip afm-chip--purple">
                      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
                        <line x1="3" y1="10" x2="21" y2="10"/>
                      </svg>
                      Date range set
                    </span>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="afm-footer">
            <div className="afm-footer-note">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/>
                <line x1="12" y1="16" x2="12.01" y2="16"/>
              </svg>
              Click Apply to update results
            </div>
            <div className="afm-footer-btns">
              <button className="afm-btn-cancel" onClick={handleClose}>Cancel</button>
              <button
                className="afm-btn-apply"
                onClick={() => {
                  setPage(1);
                  filterData();
                  setIsApplied(true);
                  setIsDirty(false);
                  onClose();
                }}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12"/>
                </svg>
                Apply Filters
              </button>
            </div>
          </div>
        </div>
      </Modal>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');

        /* Trigger buttons */
        .af-trigger-btn {
          position: relative;
          display: inline-flex; align-items: center; gap: 7px;
          font-family: 'Plus Jakarta Sans', sans-serif; font-size: 13px; font-weight: 700; color: #fff;
          padding: 9px 18px; border-radius: 10px; border: none;
          background: linear-gradient(135deg,#6d28d9,#7c3aed);
          cursor: pointer; box-shadow: 0 3px 12px rgba(124,58,237,.28);
          transition: transform .15s, box-shadow .18s;
        }
        .af-trigger-btn:hover { transform: translateY(-1px); box-shadow: 0 6px 20px rgba(124,58,237,.36); }

        .af-trigger-dot {
          position: absolute; top: 7px; right: 7px;
          width: 7px; height: 7px; border-radius: 50%;
          background: #fbbf24; border: 1.5px solid #fff;
        }

        .af-clear-btn {
          display: inline-flex; align-items: center; gap: 5px;
          font-family: 'Plus Jakarta Sans', sans-serif; font-size: 13px; font-weight: 600; color: #dc2626;
          padding: 9px 16px; border-radius: 10px;
          background: #fff7f7; border: 1.5px solid #fecaca;
          cursor: pointer; transition: all .15s;
        }
        .af-clear-btn:hover { background: #fee2e2; border-color: #fca5a5; }

        /* Slide panel */
        .afm-panel {
          font-family: 'Plus Jakarta Sans', sans-serif;
          position: fixed; top: 0; right: 0; bottom: 0;
          width: 100%; max-width: 500px;
          background: #ffffff;
          z-index: 1004;
          display: flex; flex-direction: column;
          box-shadow: -12px 0 60px rgba(15,23,42,.18);
          transition: transform .35s cubic-bezier(0.16,1,0.3,1);
          overflow: hidden;
        }
        .afm-panel--open  { transform: translateX(0); }
        .afm-panel--close { transform: translateX(100%); }

        /* Top glow */
        .afm-top-glow {
          height: 3px;
          background: linear-gradient(90deg,#4c1d95,#7c3aed,#a78bfa,#7c3aed,#4c1d95);
          background-size: 200% 100%;
          animation: afmGlow 3s linear infinite; flex-shrink: 0;
        }
        @keyframes afmGlow { 0%{background-position:0%} 100%{background-position:200%} }

        /* Header */
        .afm-header {
          padding: 15px 26px;
          background: linear-gradient(135deg,#4c1d95 0%,#6d28d9 60%,#7c3aed 100%);
          display: flex; align-items: center; justify-content: space-between; flex-shrink: 0;
        }
        .afm-header-brand { display: flex; align-items: center; gap: 13px; }
        .afm-header-logo {
          width: 42px; height: 42px; border-radius: 12px;
          background: rgba(255,255,255,.15); border: 1.5px solid rgba(255,255,255,.25);
          display: flex; align-items: center; justify-content: center; flex-shrink: 0;
        }
        .afm-header-title { font-size: 16px; font-weight: 800; color: #fff; letter-spacing: -.3px; margin-bottom: 2px; line-height: 1; }
        .afm-header-sub { font-size: 12px; color: rgba(255,255,255,.55); font-weight: 500; }
        .afm-close-btn {
          background: none; border: none; cursor: pointer;
          color: rgba(255,255,255,.65); display: flex; align-items: center; padding: 2px; transition: color .15s;
        }
        .afm-close-btn:hover { color: #fff; }

        /* Body */
        .afm-body {
          flex: 1; padding: 24px 28px; overflow-y: auto;
          display: flex; flex-direction: column; gap: 6px;
        }
        .afm-body::-webkit-scrollbar { width: 4px; }
        .afm-body::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 4px; }

        /* Section */
        .afm-section { display: flex; gap: 16px; align-items: flex-start; padding: 20px 0; }
        .afm-section-icon {
          width: 44px; height: 44px; border-radius: 12px; flex-shrink: 0;
          display: flex; align-items: center; justify-content: center; margin-top: 2px;
        }
        .afm-section-icon--green { background: linear-gradient(135deg,#f0fdf4,#dcfce7); border: 1.5px solid #bbf7d0; color: #16a34a; }
        .afm-section-icon--purple { background: linear-gradient(135deg,#faf5ff,#ede9fe); border: 1.5px solid #ddd6fe; color: #7c3aed; }
        .afm-section-content { flex: 1; display: flex; flex-direction: column; gap: 6px; }

        .afm-label {
          display: flex; align-items: center; gap: 8px;
          font-size: 13px; font-weight: 700; color: #0f172a;
        }
        .afm-active-dot { width: 7px; height: 7px; border-radius: 50%; background: #7c3aed; flex-shrink: 0; }
        .afm-hint { font-size: 12px; color: #94a3b8; font-weight: 500; margin-bottom: 4px; }

        /* Ant Select */
        .afm-ant-select .ant-select-selector {
          height: 46px !important; border: 1.5px solid #e2e8f0 !important;
          border-radius: 11px !important; background: #f8fafc !important;
          font-family: 'Plus Jakarta Sans', sans-serif !important;
        }
        .afm-ant-select .ant-select-selection-search-input { height: 44px !important; }
        .afm-ant-select .ant-select-selection-placeholder { line-height: 44px !important; color: #cbd5e1 !important; }
        .afm-ant-select .ant-select-selection-item { line-height: 44px !important; }
        .afm-ant-select .ant-select-selector:focus-within {
          border-color: #7c3aed !important;
          box-shadow: 0 0 0 3px rgba(124,58,237,.1) !important;
        }

        /* DatePicker */
        .afm-datepicker-wrap .react-datepicker-wrapper { width: 100%; }
        .afm-datepicker {
          width: 100%; padding: 12px 14px;
          background: #f8fafc; border: 1.5px solid #e2e8f0; border-radius: 11px;
          font-family: 'Plus Jakarta Sans', sans-serif; font-size: 14px; color: #0f172a;
          outline: none; transition: border-color .18s, box-shadow .18s;
          box-sizing: border-box;
        }
        .afm-datepicker:focus { border-color: #7c3aed; background: #fff; box-shadow: 0 0 0 3px rgba(124,58,237,.1); }
        .afm-datepicker::placeholder { color: #cbd5e1; }

        /* Divider */
        .afm-divider { height: 1px; background: #f1f5f9; margin: 0 -4px; }

        /* Summary */
        .afm-summary {
          margin-top: 4px; padding: 16px;
          background: linear-gradient(135deg,#faf5ff,#ede9fe);
          border: 1.5px solid #ddd6fe; border-radius: 12px;
        }
        .afm-summary-label { font-size: 11.5px; font-weight: 700; color: #6d28d9; text-transform: uppercase; letter-spacing: 0.4px; margin-bottom: 10px; }
        .afm-summary-chips { display: flex; flex-wrap: wrap; gap: 7px; }
        .afm-chip {
          display: inline-flex; align-items: center; gap: 5px;
          font-size: 12px; font-weight: 600;
          padding: 5px 10px; border-radius: 7px;
        }
        .afm-chip--green { background: #f0fdf4; border: 1.5px solid #bbf7d0; color: #15803d; }
        .afm-chip--purple { background: #ede9fe; border: 1.5px solid #c4b5fd; color: #6d28d9; }

        /* Footer */
        .afm-footer {
          padding: 14px 26px;
          border-top: 1.5px solid #f1f5f9;
          background: #fafbfc;
          display: flex; align-items: center; justify-content: space-between;
          flex-wrap: wrap; gap: 10px; flex-shrink: 0;
        }
        .afm-footer-note { display: flex; align-items: center; gap: 5px; font-size: 12px; color: #94a3b8; font-weight: 500; }
        .afm-footer-btns { display: flex; align-items: center; gap: 8px; }

        .afm-btn-cancel {
          font-family: 'Plus Jakarta Sans', sans-serif; font-size: 13px; font-weight: 600; color: #64748b;
          padding: 9px 16px; border-radius: 10px; border: 1.5px solid #e2e8f0; background: #fff;
          cursor: pointer; transition: all .15s;
        }
        .afm-btn-cancel:hover { background: #f1f5f9; color: #374151; }

        .afm-btn-apply {
          display: inline-flex; align-items: center; gap: 7px;
          font-family: 'Plus Jakarta Sans', sans-serif; font-size: 13.5px; font-weight: 700; color: #fff;
          padding: 10px 22px; border-radius: 10px; border: none;
          background: linear-gradient(135deg,#6d28d9,#7c3aed);
          cursor: pointer; box-shadow: 0 4px 14px rgba(124,58,237,.3);
          transition: transform .15s, box-shadow .18s; position: relative; overflow: hidden;
        }
        .afm-btn-apply::after { content: ''; position: absolute; inset: 0; background: linear-gradient(135deg,rgba(255,255,255,.1),transparent 60%); }
        .afm-btn-apply:hover { transform: translateY(-1px); box-shadow: 0 8px 24px rgba(124,58,237,.4); }

        @media (max-width: 520px) {
          .afm-panel { max-width: 100%; }
          .afm-header, .afm-body, .afm-footer { padding-left: 18px; padding-right: 18px; }
        }
      `}</style>
    </>
  );
};

export default FilterModal;